import { useStore } from 'hooks/useStore';
import { observer } from 'mobx-react';
import { Button } from 'primereact/button';
import { OverlayPanel } from 'primereact/overlaypanel';
import { Skeleton } from 'primereact/skeleton';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { NotificationListItem } from 'services/notificationService';
import NotificationItem from './NotificationItem';
import classes from './notificationPopup.module.scss';

interface NotificationPopupProps {
  target: React.RefObject<HTMLElement>;
  visible: boolean;
  onHide: () => void;
}

const NotificationPopup: React.FC<NotificationPopupProps> = observer(
  ({ target, visible, onHide }) => {
    const op = useRef<OverlayPanel>(null);
    const archivedScrollRef = useRef<HTMLDivElement>(null);
    const { NotificationStore } = useStore();
    const [activeTab, setActiveTab] = useState<'unread' | 'archived'>('unread');

    // Destructure observable properties (safe to destructure)
    const {
      unreadNotifications,
      archivedNotifications,
      isLoadingUnread,
      isLoadingArchived,
      archivedHasMore,
    } = NotificationStore;

    // Show/hide overlay panel
    useEffect(() => {
      if (visible && target.current && op.current) {
        op.current.show(null, target.current);
      } else if (!visible && op.current) {
        op.current.hide();
      }
    }, [visible, target]);

    // Handle overlay panel show event to fetch notifications
    const handleOverlayShow = useCallback(() => {
      // Only fetch archived if not already loaded
      if (NotificationStore.archivedNotifications.length === 0) {
        NotificationStore.fetchArchivedNotifications(1, false);
      }
    }, [NotificationStore]);

    // Infinite scroll handler for archived tab
    const handleArchivedScroll = useCallback(() => {
      const element = archivedScrollRef.current;
      if (!element || NotificationStore.isLoadingArchived || !NotificationStore.archivedHasMore)
        return;

      const { scrollTop, scrollHeight, clientHeight } = element;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

      // Load more when within 100px of bottom
      if (distanceFromBottom < 100) {
        NotificationStore.loadMoreArchived();
      }
    }, [NotificationStore]);

    // Attach scroll listener
    useEffect(() => {
      const element = archivedScrollRef.current;
      if (element && activeTab === 'archived') {
        element.addEventListener('scroll', handleArchivedScroll);
        return () => {
          element.removeEventListener('scroll', handleArchivedScroll);
        };
      }
    }, [handleArchivedScroll, activeTab]);

    const handleMarkAllAsRead = async () => {
      try {
        await NotificationStore.markAllAsRead();
        // Lists are automatically refreshed by the store
      } catch (error) {
        console.error('Failed to mark all as read:', error);
      }
    };

    const handleMarkAsRead = async (notificationId: number) => {
      try {
        await NotificationStore.markNotificationAsRead(notificationId);
      } catch (error) {
        console.error('Failed to mark notification as read:', error);
      }
    };

    const handleNotificationClick = (notification: NotificationListItem) => {
      // Handle notification click (e.g., navigate to related page)
      // This can be extended based on notification_type
      if (notification.metadata?.bid_id) {
        // Could navigate to bid page
        console.log('Navigate to bid:', notification.metadata.bid_id);
      }
    };

    const LoadingSkeleton = () => (
      <div className={classes.loadingState}>
        {[1, 2, 3].map(i => (
          <div
            key={i}
            className={classes.skeletonItem}
            style={{ width: '100%', marginBottom: '1rem' }}
          >
            <div
              style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}
            >
              <Skeleton width="60%" height="1rem" />
              <Skeleton width="10%" height="1rem" />
            </div>
            <Skeleton width="100%" height="2.5rem" />
          </div>
        ))}
      </div>
    );

    return (
      <OverlayPanel
        ref={op}
        className={classes.notificationOverlay}
        onHide={onHide}
        onShow={handleOverlayShow}
        dismissable
        showCloseIcon={false}
      >
        <div className={classes.notificationPopup}>
          <div className={classes.popupHeader}>
            <h3 className={classes.popupTitle}>Notifications</h3>
          </div>

          <div className={classes.tabsContainer}>
            <div
              className={`${classes.tab} ${activeTab === 'unread' ? classes.active : ''}`}
              onClick={() => setActiveTab('unread')}
            >
              Unread {unreadNotifications.length > 0 && `(${unreadNotifications.length})`}
            </div>
            <div
              className={`${classes.tab} ${activeTab === 'archived' ? classes.active : ''}`}
              onClick={() => setActiveTab('archived')}
            >
              Archived
            </div>
          </div>

          <div className={classes.tabContent}>
            {activeTab === 'unread' ? (
              <>
                {unreadNotifications.length > 0 && (
                  <div className={classes.markAllButtonContainer}>
                    <Button
                      label="Mark All as Read"
                      onClick={handleMarkAllAsRead}
                      className={classes.markAllButton}
                      size="small"
                      disabled={isLoadingUnread}
                    />
                  </div>
                )}

                {isLoadingUnread && unreadNotifications.length === 0 ? (
                  <LoadingSkeleton />
                ) : unreadNotifications.length === 0 ? (
                  <div className={classes.emptyState}>
                    <i className="pi pi-bell-slash" />
                    <p>No unread notifications</p>
                  </div>
                ) : (
                  <div className={classes.notificationList}>
                    {unreadNotifications.map(notification => (
                      <NotificationItem
                        key={notification.id}
                        notification={notification}
                        onMarkAsRead={handleMarkAsRead}
                        onClick={handleNotificationClick}
                      />
                    ))}
                    {isLoadingUnread && (
                      <div className={classes.loadingMore}>
                        <i className="pi pi-spin pi-spinner" />
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              <div className={classes.archivedTabContent} ref={archivedScrollRef}>
                {isLoadingArchived && archivedNotifications.length === 0 ? (
                  <LoadingSkeleton />
                ) : archivedNotifications.length === 0 ? (
                  <div className={classes.emptyState}>
                    <i className="pi pi-inbox" />
                    <p>No archived notifications</p>
                  </div>
                ) : (
                  <>
                    {archivedNotifications.map(notification => (
                      <NotificationItem
                        key={notification.id}
                        notification={notification}
                        onMarkAsRead={handleMarkAsRead}
                        onClick={handleNotificationClick}
                      />
                    ))}
                    {isLoadingArchived && (
                      <div className={classes.loadingMore}>
                        <i className="pi pi-spin pi-spinner" />
                      </div>
                    )}
                    {!archivedHasMore && archivedNotifications.length > 0 && (
                      <div className={classes.endOfList}>
                        <span>No more notifications</span>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </OverlayPanel>
    );
  }
);

export default NotificationPopup;
